import React, { createContext, useContext, useMemo } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { TableRow } from "@/src/components/ui/table";
import { cn } from "@/src/lib/utils";

import type { ComponentProps, JSX } from "react";

import type { DragHandleContextValue } from "./dataTable.types";

// Fourni par la ligne à sa poignée : seule la poignée saisit la ligne.
const DragHandleContext = createContext<DragHandleContextValue | null>(null);

export function useDragHandle(): DragHandleContextValue | null {
  return useContext(DragHandleContext);
}

type DataTableSortableRowProps = Pick<
  ComponentProps<typeof TableRow>,
  "className" | "children" | "onAuxClick" | "onClick"
> & {
  rowId: string;
};

function DataTableSortableRow({
  rowId,
  className,
  children,
  onAuxClick,
  onClick,
}: DataTableSortableRowProps): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId });

  const handle = useMemo(
    () => ({ attributes, listeners, setActivatorNodeRef }),
    [attributes, listeners, setActivatorNodeRef],
  );

  return (
    <TableRow
      ref={setNodeRef}
      className={cn("relative", isDragging && "z-10 opacity-50", className)}
      onAuxClick={onAuxClick}
      onClick={onClick}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <DragHandleContext.Provider value={handle}>
        {children}
      </DragHandleContext.Provider>
    </TableRow>
  );
}

export default DataTableSortableRow;
